export interface EInvoiceFieldSelection {
  modelPath: string;
  term?: string;
  termPath?: string;
  src?: string;
  valuePreview?: string;
}

export interface EInvoiceProvenanceIndex {
  byModelPath: Map<string, EInvoiceFieldSelection>;
  bySourcePath: Map<string, EInvoiceFieldSelection[]>;
  byTerm: Map<string, EInvoiceFieldSelection[]>;
  orderedModelPaths: string[];
}

const METADATA_KEYS = new Set(['$provenance', '$src']);
const PREVIEW_LIMIT = 120;

export function buildProvenanceIndex(model: unknown): EInvoiceProvenanceIndex {
  const byModelPath = new Map<string, EInvoiceFieldSelection>();
  const bySourcePath = new Map<string, EInvoiceFieldSelection[]>();
  const byTerm = new Map<string, EInvoiceFieldSelection[]>();
  const orderedModelPaths: string[] = [];

  const visit = (value: unknown): void => {
    if (value == null || typeof value !== 'object') {
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        visit(item);
      }
      return;
    }

    const node = value as Record<string, unknown>;
    const selection = createSelectionFromNode(node);
    if (selection && !byModelPath.has(selection.modelPath)) {
      byModelPath.set(selection.modelPath, selection);
      orderedModelPaths.push(selection.modelPath);

      if (selection.src) {
        addToMap(bySourcePath, selection.src, selection);
      }
      if (selection.term) {
        addToMap(byTerm, selection.term, selection);
      }
    }

    for (const [key, child] of Object.entries(node)) {
      if (!METADATA_KEYS.has(key)) {
        visit(child);
      }
    }
  };

  visit(model);

  return {
    byModelPath,
    bySourcePath,
    byTerm,
    orderedModelPaths
  };
}

export function createSelectionFromNode(node: Record<string, unknown>): EInvoiceFieldSelection | undefined {
  const provenance = node['$provenance'] as Record<string, unknown> | undefined;
  const modelPath = provenance?.['modelPath'];
  if (typeof modelPath !== 'string' || modelPath.length === 0) {
    return undefined;
  }

  return {
    modelPath,
    term: readString(provenance?.['term']),
    termPath: readString(provenance?.['termPath']),
    src: extractSourcePath(node['$src']),
    valuePreview: createValuePreview(node['value'])
  };
}

export function extractSourcePath(source: unknown, preferredKey = 'value'): string | undefined {
  if (typeof source === 'string') {
    return source;
  }

  if (Array.isArray(source)) {
    return source.find((item): item is string => typeof item === 'string');
  }

  if (source == null || typeof source !== 'object') {
    return undefined;
  }

  const sourceMap = source as Record<string, unknown>;
  const preferred = sourceMap[preferredKey];
  const preferredPath = readSourceValue(preferred);
  if (preferredPath) {
    return preferredPath;
  }

  for (const value of Object.values(sourceMap)) {
    const path = readSourceValue(value);
    if (path) {
      return path;
    }
  }

  return undefined;
}

function readSourceValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === 'string');
  }

  return undefined;
}

function createValuePreview(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }

  const preview = String(value);
  if (preview.length <= PREVIEW_LIMIT) {
    return preview;
  }

  return `${preview.slice(0, PREVIEW_LIMIT)}...`;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function addToMap<TKey, TValue>(map: Map<TKey, TValue[]>, key: TKey, value: TValue): void {
  const values = map.get(key);
  if (values) {
    values.push(value);
  } else {
    map.set(key, [value]);
  }
}